import { useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronDown, ChevronRight, Inbox } from 'lucide-react';
import {
  EmptyState,
  HelpAnchor,
  SkeletonBlock,
  SkeletonCardGrid,
  SurfacePageBody,
  SurfacePageHeader,
  SurfacePageLayout,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { AskStrip } from '@/versions/v1/chrome-oem/AskStrip';
import { MINES, MINE_ORDER } from '@/versions/v1/data/core';
import { unpackTable } from '@/versions/v1/shared/columnar';
import { useOemData, type WorkOrder } from '@/versions/v1/shared/useOemData';
import { jobs as buildJobs, type OemJob } from '@/versions/v1/shared/oem-engine';
import { routeForView } from '@/versions/v1/shared/oem-routes';
import { MeasureTile, SceneSection, TonePill } from '@/versions/v1/shared/oem-tiles';
import { useSeedNav, type AuditTables } from '@/versions/v1/modules/workflows/use-seed-nav';
import { JobCardTile } from './JobCardTile';
import { jobNav } from './job-nav';
import {
  applyJobFilters,
  jobBoardSummary,
  jobCards,
  jobKinds,
  jobMines,
  jobModuleRows,
  noFilters,
  parseJobFilters,
  type JobCard,
} from './JobBoardScene.transform';

// =========================================================================
// Job board. Route `/job-board`. Fork-owned (zone C), P5b lane.
//
// Port of the demo's `renderJobboard` (source line 3051), both modes.
//
// The demo's insight — and the reason this page is not a kanban clone — is
// that ten columns of cards do not fit on one screen, so the default view is
// one **row** per module: count, severity, the kinds inside it, a progress
// bar and the machines involved. Expanding a row drops its cards in place.
// The full ten-column board is one click away for anyone who wants it.
//
// Filters live in the URL (`?mine=`, `?sev=`, `?kind=`) so a filtered board
// is a link somebody can send, and so back/forward move between filter states
// rather than leaving the page.
//
// A card opens the job itself — the alert, the incident, the machine, the
// technician job, the case rows — exactly where the demo's `open()` went
// (`OemJob.action`, resolved by `job-nav.ts`). On the full board each column
// header opens its module page, as the demo's `.bcol h4` did.
//
// UI layer.
// =========================================================================

/** Work orders, technician jobs and cases — what `buildJobs` reads. */
function useJobsData() {
  const workOrders = useOemData('work-orders');
  const service = useOemData('service');

  const loading = workOrders.loading || service.loading;
  const error = workOrders.error ?? service.error ?? null;

  const all = useMemo<OemJob[] | null>(() => {
    if (!workOrders.data || !service.data) return null;
    return buildJobs({
      workOrders: unpackTable<WorkOrder>(workOrders.data.wos),
      techJobs: service.data.tapp_jobs,
      cases: service.data.cases,
    });
  }, [workOrders.data, service.data]);

  const cases = service.data?.cases;
  return { all, cases, loading, error };
}

export function JobBoardScene() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { all, cases, loading, error } = useJobsData();
  // The case rows' `open()` was `audit('cases', { case_no })`, so the case
  // table rides along for the source drawer.
  const seedNav = useSeedNav(
    useMemo(
      (): AuditTables =>
        cases ? { cases: cases as unknown as readonly Record<string, unknown>[] } : {},
      [cases],
    ),
  );

  const mode = params.get('mode') === 'board' ? 'board' : 'summary';
  const openModule = params.get('module');

  const kinds = useMemo(() => (all ? jobKinds(all) : []), [all]);
  const mines = useMemo(() => (all ? jobMines(all, MINE_ORDER) : []), [all]);

  const filters = useMemo(
    () =>
      parseJobFilters(
        (key) => params.get(key),
        MINE_ORDER,
        kinds.map((k) => k.kind),
      ),
    [params, kinds],
  );

  const jobs = useMemo(
    () => (all ? applyJobFilters(all, filters) : []),
    [all, filters],
  );
  const summary = useMemo(() => jobBoardSummary(jobs), [jobs]);
  const rows = useMemo(() => jobModuleRows(jobs), [jobs]);

  /** Write one URL parameter, dropping it when the value is the default. */
  const setParam = useCallback(
    (key: string, value: string | null) => {
      const next = new URLSearchParams(params);
      if (value === null || value === '') next.delete(key);
      else next.set(key, value);
      setParams(next, { replace: false });
    },
    [params, setParams],
  );

  const openJob = useCallback(
    (card: JobCard) => seedNav.dispatch(jobNav(card.job)),
    [seedNav],
  );

  /** Demo `.bcol h4` — `show('module', id)`. */
  const openModulePage = useCallback(
    (id: string) => {
      const to = routeForView('module', id);
      if (to) navigate(to);
    },
    [navigate],
  );

  return (
    <SurfacePageLayout width="full">
      <Commentable name="JobBoardScene">
        <SurfacePageBody className="@container max-w-[1400px] space-y-8 pt-6">
          <SurfacePageHeader
            kicker="Home"
            title="Job board"
            description="Every open job in the platform — alerts, incidents, cases, agent drafts, jobs in progress, watch conditions, capacity and commercial items — at the workflow module it has reached. The summary fits on one screen; expand a module, or the whole board, for the cards and their progress."
            actions={<HelpAnchor doc="workflows/job-board" />}
            meta={
              all ? (
                <span className="mono text-[11.5px]" style={{ color: 'var(--ink-tertiary)' }}>
                  {`${summary.total} open · ${summary.critical} critical`}
                </span>
              ) : null
            }
          />

          <AskStrip view="jobboard" />

          {error ? (
            <div
              role="alert"
              className="rounded-xl border p-4 text-sm"
              style={{
                borderColor: 'var(--hairline)',
                background: 'var(--crit-soft)',
                color: 'var(--crit)',
              }}
            >
              {`The board could not be loaded (${error.message}). Nothing is shown rather than a
              partial board — a job board missing a column is worse than one that is empty.`}
            </div>
          ) : null}

          {/* ------------------------------------------------------- */}
          {/* Filters + mode                                          */}
          {/* ------------------------------------------------------- */}
          <Commentable name="Filters">
            <div className="flex flex-wrap items-end gap-3">
              <Select
                label="Site"
                value={filters.mine ?? ''}
                onChange={(v) => setParam('mine', v)}
                options={[
                  { value: '', label: 'All sites' },
                  ...mines.map((m) => ({ value: m.mine, label: MINES[m.mine].name })),
                ]}
              />
              <Select
                label="Severity"
                value={filters.sev ?? ''}
                onChange={(v) => setParam('sev', v)}
                options={[
                  { value: '', label: 'Any severity' },
                  { value: 'crit', label: 'Critical' },
                  { value: 'warn', label: 'Watch' },
                  { value: 'info', label: 'In train' },
                  { value: 'done', label: 'Closed' },
                ]}
              />
              <Select
                label="Kind"
                value={filters.kind ?? ''}
                onChange={(v) => setParam('kind', v)}
                options={[
                  { value: '', label: 'Any kind' },
                  ...kinds.map((k) => ({ value: k.kind, label: `${k.kind} (${k.n})` })),
                ]}
              />

              <div className="ml-auto flex items-center gap-1 rounded-lg border p-0.5"
                style={{ borderColor: 'var(--hairline)', background: 'var(--surface-2)' }}
              >
                {(['summary', 'board'] as const).map((m) => (
                  <button
                    key={m}
                    type="button"
                    aria-pressed={mode === m}
                    onClick={() => setParam('mode', m === 'summary' ? null : m)}
                    className="rounded-md px-3 py-1.5 text-[11.5px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
                    style={
                      mode === m
                        ? { background: 'var(--accent)', color: 'var(--surface-2)' }
                        : { color: 'var(--ink-secondary)' }
                    }
                  >
                    {m === 'summary' ? 'Summary' : 'Full board'}
                  </button>
                ))}
              </div>

              {!noFilters(filters) ? (
                <button
                  type="button"
                  onClick={() => {
                    const next = new URLSearchParams(params);
                    next.delete('mine');
                    next.delete('sev');
                    next.delete('kind');
                    setParams(next);
                  }}
                  className="rounded-md px-2 py-1.5 text-[11.5px] font-medium underline decoration-dotted underline-offset-[3px]"
                  style={{ color: 'var(--accent)' }}
                >
                  Clear filters
                </button>
              ) : null}
            </div>
          </Commentable>

          {/* ------------------------------------------------------- */}
          {/* Summary figures                                         */}
          {/* ------------------------------------------------------- */}
          <Commentable name="Summary">
            {all ? (
              <div className="grid grid-cols-2 gap-3 @2xl:grid-cols-3 @5xl:grid-cols-6">
                <MeasureTile
                  eyebrow="Critical"
                  value={summary.critical}
                  tone={summary.critical ? 'bad' : 'good'}
                />
                <MeasureTile
                  eyebrow="Waiting on a person"
                  value={summary.waiting}
                  tone={summary.waiting ? 'warn' : 'good'}
                />
                <MeasureTile eyebrow="In progress" value={summary.inProgress} />
                <MeasureTile
                  eyebrow="Closed this fortnight"
                  value={summary.closed}
                  tone="good"
                />
                <MeasureTile eyebrow="Sites" value={summary.sites} />
                <MeasureTile eyebrow="Machines" value={summary.machines} />
              </div>
            ) : (
              <SkeletonCardGrid count={6} cols={3} />
            )}
          </Commentable>

          {/* ------------------------------------------------------- */}
          {/* The board                                               */}
          {/* ------------------------------------------------------- */}
          <Commentable name="Board">
            {!all ? (
              <SkeletonBlock className="h-96 rounded-xl" />
            ) : jobs.length === 0 ? (
              <EmptyState
                icon={<Inbox className="h-8 w-8 text-zinc-400" aria-hidden="true" />}
                title="No jobs match these filters"
                description="Clear a filter to bring the rest of the board back."
              />
            ) : mode === 'board' ? (
              <SceneSection
                title="Every module, every job"
                subtitle="the operating model left to right"
              >
                <div className="overflow-x-auto pb-2">
                  <div className="flex min-w-max gap-3">
                    {rows.map((row) => (
                      <div key={row.id} className="w-[19rem] flex-none">
                        <div className="mb-2 flex items-baseline justify-between gap-2">
                          <h3
                            className="text-[12.5px] font-semibold"
                            style={{ color: 'var(--ink-primary)' }}
                          >
                            <button
                              type="button"
                              onClick={() => openModulePage(row.id)}
                              title={`Open ${row.name}`}
                              className="rounded text-left underline decoration-transparent underline-offset-[3px] transition-colors hover:decoration-current focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
                            >
                              <span className="mono" style={{ color: row.color }}>
                                {row.n}
                              </span>{' '}
                              {row.short}
                            </button>
                          </h3>
                          <span
                            className="mono text-[11.5px]"
                            style={{ color: 'var(--ink-tertiary)' }}
                          >
                            {row.jobs.length}
                          </span>
                        </div>
                        <div className="flex flex-col gap-2">
                          {row.jobs.length === 0 ? (
                            <p
                              className="rounded-xl border border-dashed px-3 py-4 text-center text-[11.5px]"
                              style={{
                                borderColor: 'var(--hairline)',
                                color: 'var(--ink-tertiary)',
                              }}
                            >
                              Nothing at this module
                            </p>
                          ) : (
                            jobCards(row.jobs).map((card) => (
                              <JobCardTile key={card.key} card={card} onOpen={openJob} />
                            ))
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </SceneSection>
            ) : (
              <SceneSection
                title="Where the jobs are"
                subtitle="one row per module · open a row for its cards"
              >
                <ul
                  className="grid list-none grid-cols-1 gap-px overflow-hidden rounded-xl border p-0"
                  style={{ borderColor: 'var(--hairline)', background: 'var(--hairline)' }}
                >
                  {rows.map((row) => {
                    const expanded = openModule === row.id;
                    return (
                      <li key={row.id} style={{ background: 'var(--surface-2)' }}>
                        <button
                          type="button"
                          aria-expanded={expanded}
                          disabled={row.jobs.length === 0}
                          onClick={() => setParam('module', expanded ? null : row.id)}
                          className="grid w-full grid-cols-[auto_minmax(0,11rem)_auto_minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1 px-4 py-3 text-left transition-colors enabled:hover:bg-[var(--surface-1)] disabled:cursor-default disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
                        >
                          <span
                            className="mono text-[12px] font-semibold"
                            style={{ color: row.color }}
                          >
                            {row.n}
                          </span>
                          <span
                            className="truncate text-[12.5px] font-medium"
                            style={{ color: 'var(--ink-primary)' }}
                          >
                            {row.short}
                          </span>
                          <span
                            className="mono w-8 text-right text-[12.5px] font-semibold"
                            style={{
                              color: row.jobs.length
                                ? 'var(--ink-primary)'
                                : 'var(--ink-tertiary)',
                            }}
                          >
                            {row.jobs.length}
                          </span>
                          <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                            {row.critical ? (
                              <TonePill tone="bad">{`${row.critical} critical`}</TonePill>
                            ) : null}
                            {row.waiting ? (
                              <TonePill tone="warn">{`${row.waiting} waiting on a person`}</TonePill>
                            ) : null}
                            <span
                              className="truncate text-[11px]"
                              style={{ color: 'var(--ink-tertiary)' }}
                            >
                              {row.kinds.join(' · ') || 'nothing waiting'}
                            </span>
                          </span>
                          <span className="flex items-center gap-3">
                            <span
                              aria-hidden="true"
                              className="hidden h-1.5 w-24 overflow-hidden rounded-full @3xl:block"
                              style={{ background: 'var(--hairline)' }}
                            >
                              <span
                                className="block h-full rounded-full"
                                style={{ width: `${row.avg}%`, background: row.color }}
                              />
                            </span>
                            <span
                              className="mono w-10 text-right text-[11.5px]"
                              style={{ color: 'var(--ink-tertiary)' }}
                            >
                              {row.jobs.length ? `${row.avg}%` : '—'}
                            </span>
                            {row.jobs.length ? (
                              expanded ? (
                                <ChevronDown
                                  className="h-4 w-4"
                                  style={{ color: 'var(--ink-tertiary)' }}
                                  aria-hidden="true"
                                />
                              ) : (
                                <ChevronRight
                                  className="h-4 w-4"
                                  style={{ color: 'var(--ink-tertiary)' }}
                                  aria-hidden="true"
                                />
                              )
                            ) : (
                              <span className="h-4 w-4" />
                            )}
                          </span>
                        </button>

                        {expanded && row.jobs.length ? (
                          <div className="grid grid-cols-1 gap-2 px-4 pb-4 @2xl:grid-cols-2 @5xl:grid-cols-3">
                            {jobCards(row.jobs).map((card) => (
                              <JobCardTile key={card.key} card={card} onOpen={openJob} />
                            ))}
                          </div>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
                <p
                  className="mt-3 text-[11.5px] leading-[1.6]"
                  style={{ color: 'var(--ink-tertiary)' }}
                >
                  A card opens the job itself. Its run link follows the job through all ten
                  modules — three jobs carry a worked trace; the rest are generated from the
                  module they have reached.
                </p>
              </SceneSection>
            )}
          </Commentable>

          <p className="sr-only" role="status">
            {loading && !all
              ? 'Loading the job board.'
              : `${summary.total} jobs on the board, ${summary.critical} critical.`}
          </p>
        </SurfacePageBody>
      </Commentable>
    </SurfacePageLayout>
  );
}

/** A labelled native select — the demo's `popfilt` controls. */
function Select({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <label className="flex flex-col gap-1">
      <span
        className="text-[10px] font-semibold uppercase tracking-[0.08em]"
        style={{ color: 'var(--ink-tertiary)' }}
      >
        {label}
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 rounded-lg border px-2 text-[12px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
        style={{
          borderColor: 'var(--hairline)',
          background: 'var(--surface-2)',
          color: 'var(--ink-primary)',
        }}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

export default JobBoardScene;
